/**
 * Chi tiết 1 chủ đề thảo luận (plan 2026-10-03 §2): nội dung + bài trả lời (1 cấp),
 * trả lời, sửa/xoá bài mình, đánh dấu "đã giải đáp", theo dõi, báo cáo; GV/quản trị:
 * ghim, khoá, gỡ bài (có lý do, giữ hồ sơ).
 */
import { useState } from "react";
import {
  Alert,
  Button,
  Card,
  Divider,
  Dropdown,
  Result,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  ArrowLeftOutlined,
  BellFilled,
  BellOutlined,
  CheckCircleFilled,
  LockOutlined,
  MoreOutlined,
  PushpinOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  type ForumPost,
  type ForumThreadDetail,
  forumErrorMessage,
  useAcceptPost,
  useDeletePost,
  useDeleteThread,
  useForumThread,
  useModerateThread,
  useReply,
  useReportForum,
  useSubscribeThread,
  useUpdatePost,
  useUpdateThread,
} from "@/api/forum";
import { MarkdownContent } from "@/components/MarkdownContent";
import { ForumEditor } from "./ForumEditor";
import { confirmDelete, focusForumEditor, promptReason } from "./forumDialogs";

const { Title, Text } = Typography;

function AuthorLine({
  name,
  isStaff,
  at,
  edited,
}: {
  name: string;
  isStaff: boolean;
  at: string;
  edited?: string | null;
}) {
  return (
    <Space size={6} wrap>
      <Text strong>{name}</Text>
      {isStaff && <Tag color="gold">Giảng viên</Tag>}
      <Text type="secondary" style={{ fontSize: 12 }}>
        {dayjs(at).format("HH:mm DD/MM/YYYY")}
        {edited ? " · đã sửa" : ""}
      </Text>
    </Space>
  );
}

function DeletedBody({
  label,
  removedBody,
  reason,
}: {
  label: string;
  removedBody: string;
  reason: string;
}) {
  return (
    <div>
      <Text type="secondary" italic>
        {label}
      </Text>
      {removedBody && (
        <Alert
          style={{ marginTop: 8 }}
          type="warning"
          message={`Chỉ người kiểm duyệt thấy — lý do gỡ: ${reason || "—"}`}
          description={<Text style={{ whiteSpace: "pre-wrap" }}>{removedBody}</Text>}
        />
      )}
    </div>
  );
}

function Editor({
  initial,
  onSave,
  onCancel,
  saving,
}: {
  initial: string;
  onSave: (v: string) => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const [value, setValue] = useState(initial);
  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      <ForumEditor value={value} onChange={setValue} height={260} />
      <Space>
        <Button
          type="primary"
          size="small"
          loading={saving}
          disabled={!value.trim()}
          onClick={() => onSave(value)}
        >
          Lưu
        </Button>
        <Button size="small" onClick={onCancel}>
          Huỷ
        </Button>
      </Space>
    </Space>
  );
}

function PostItem({
  post,
  thread,
  replies,
  onReply,
}: {
  post: ForumPost;
  thread: ForumThreadDetail;
  replies?: ForumPost[];
  onReply?: (parent: ForumPost) => void;
}) {
  const [editing, setEditing] = useState(false);
  const update = useUpdatePost();
  const del = useDeletePost();
  const accept = useAcceptPost();
  const report = useReportForum();

  const handleDelete = async () => {
    const own = post.can_edit;
    let reason: string | null = "";
    if (own) {
      if (!(await confirmDelete("bài trả lời"))) return;
    } else {
      reason = await promptReason({
        title: "Gỡ bài trả lời",
        placeholder: "Lý do gỡ (ghi vào nhật ký kiểm duyệt)",
        okText: "Gỡ bài",
        danger: true,
      });
      if (reason === null) return;
    }
    del.mutate(
      { id: post.id, reason },
      { onError: (e) => message.error(forumErrorMessage(e, "Không xoá được")) },
    );
  };

  const handleReport = async () => {
    const reason = await promptReason({
      title: "Báo cáo vi phạm",
      placeholder: "Bài này vi phạm điều gì?",
      okText: "Gửi báo cáo",
    });
    if (!reason) return;
    report.mutate(
      { thread: thread.id, post: post.id, reason },
      { onSuccess: () => message.success("Đã gửi báo cáo tới giảng viên/quản trị") },
    );
  };

  const menuItems = [
    post.can_edit && { key: "edit", label: "Sửa", onClick: () => setEditing(true) },
    post.can_delete && {
      key: "delete",
      label: post.can_edit ? "Xoá" : "Gỡ bài",
      danger: true,
      onClick: handleDelete,
    },
    !post.is_deleted &&
      !post.can_edit && { key: "report", label: "Báo cáo vi phạm", onClick: handleReport },
  ].filter(Boolean) as { key: string; label: string; danger?: boolean; onClick: () => void }[];

  return (
    <div
      style={{
        padding: "12px 14px",
        borderRadius: 8,
        border: post.is_accepted ? "1px solid #52c41a" : "1px solid #f0f0f0",
        background: post.is_instructor_reply ? "#fffbe6" : "#fff",
        marginLeft: post.parent ? 32 : 0,
      }}
    >
      <Space style={{ width: "100%", justifyContent: "space-between" }} align="start">
        <AuthorLine
          name={post.author.name}
          isStaff={post.is_instructor_reply}
          at={post.created_at}
          edited={post.edited_at}
        />
        <Space size={4}>
          {post.is_accepted && (
            <Tag icon={<CheckCircleFilled />} color="success">
              Câu trả lời được chọn
            </Tag>
          )}
          {menuItems.length > 0 && (
            <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
              <Button type="text" size="small" icon={<MoreOutlined />} aria-label="Thao tác" />
            </Dropdown>
          )}
        </Space>
      </Space>
      <div style={{ marginTop: 6 }}>
        {post.is_deleted ? (
          <DeletedBody
            label={post.deleted_label}
            removedBody={post.removed_body}
            reason={post.deleted_reason}
          />
        ) : editing ? (
          <Editor
            initial={post.body}
            saving={update.isPending}
            onCancel={() => setEditing(false)}
            onSave={(body) =>
              update.mutate({ id: post.id, body }, { onSuccess: () => setEditing(false) })
            }
          />
        ) : (
          <MarkdownContent source={post.body} />
        )}
      </div>
      {!post.is_deleted &&
        !editing &&
        ((onReply && thread.permissions.can_reply) || thread.permissions.can_accept) && (
          <Space size={0}>
            {onReply && thread.permissions.can_reply && (
              <Button
                type="link"
                size="small"
                style={{ paddingLeft: 0 }}
                onClick={() => onReply(post)}
              >
                Trả lời
              </Button>
            )}
            {thread.permissions.can_accept && !post.is_accepted && (
              <Button
                type="link"
                size="small"
                onClick={() => accept.mutate({ id: thread.id, post: post.id })}
                loading={accept.isPending}
              >
                Chọn là câu trả lời
              </Button>
            )}
            {thread.permissions.can_accept && post.is_accepted && (
              <Button
                type="link"
                size="small"
                onClick={() => accept.mutate({ id: thread.id, post: null })}
              >
                Bỏ chọn
              </Button>
            )}
          </Space>
        )}
      {replies?.length ? (
        <Space direction="vertical" style={{ width: "100%", marginTop: 8 }}>
          {replies.map((r) => (
            <PostItem key={r.id} post={r} thread={thread} />
          ))}
        </Space>
      ) : null}
    </div>
  );
}

export function ForumThreadView({ threadId, onBack }: { threadId: number; onBack: () => void }) {
  const { data: thread, isLoading, error } = useForumThread(threadId);
  const reply = useReply();
  const updateThread = useUpdateThread();
  const delThread = useDeleteThread();
  const moderate = useModerateThread();
  const subscribe = useSubscribeThread();
  const report = useReportForum();
  const [body, setBody] = useState("");
  const [parent, setParent] = useState<ForumPost | null>(null);
  const [editing, setEditing] = useState(false);

  if (isLoading) {
    return (
      <div style={{ textAlign: "center", padding: 48 }}>
        <Spin />
      </div>
    );
  }
  if (error || !thread) {
    return (
      <Result
        status="404"
        title="Không mở được chủ đề"
        subTitle="Chủ đề không tồn tại hoặc bạn không có quyền xem."
        extra={<Button onClick={onBack}>Về danh sách</Button>}
      />
    );
  }

  const p = thread.permissions;
  const topLevel = thread.posts.filter((x) => !x.parent);
  const repliesOf = (id: number) => thread.posts.filter((x) => x.parent === id);

  const sendReply = () =>
    reply.mutate(
      { thread: thread.id, body, parent: parent?.id },
      {
        onSuccess: () => {
          setBody("");
          setParent(null);
          message.success("Đã gửi trả lời");
        },
        onError: (e) => message.error(forumErrorMessage(e, "Không gửi được trả lời")),
      },
    );

  const handleDeleteThread = async () => {
    let reason: string | null = "";
    if (p.can_edit) {
      if (!(await confirmDelete("chủ đề"))) return;
    } else {
      reason = await promptReason({
        title: "Gỡ chủ đề",
        placeholder: "Lý do gỡ (ghi vào nhật ký kiểm duyệt)",
        okText: "Gỡ chủ đề",
        danger: true,
      });
      if (reason === null) return;
    }
    delThread.mutate({ id: thread.id, reason }, { onSuccess: onBack });
  };

  const handleReport = async () => {
    const reason = await promptReason({
      title: "Báo cáo vi phạm",
      placeholder: "Chủ đề này vi phạm điều gì?",
      okText: "Gửi báo cáo",
    });
    if (!reason) return;
    report.mutate(
      { thread: thread.id, reason },
      { onSuccess: () => message.success("Đã gửi báo cáo") },
    );
  };

  const moreItems = [
    p.can_edit && { key: "edit", label: "Sửa chủ đề", onClick: () => setEditing(true) },
    p.can_delete && {
      key: "delete",
      label: p.can_edit ? "Xoá chủ đề" : "Gỡ chủ đề",
      danger: true,
      onClick: handleDeleteThread,
    },
    p.can_moderate && {
      key: "pin",
      label: thread.is_pinned ? "Bỏ ghim" : "Ghim lên đầu",
      onClick: () => moderate.mutate({ id: thread.id, action: thread.is_pinned ? "unpin" : "pin" }),
    },
    p.can_moderate && {
      key: "lock",
      label: thread.is_locked ? "Mở khoá trả lời" : "Khoá trả lời",
      onClick: () =>
        moderate.mutate({ id: thread.id, action: thread.is_locked ? "unlock" : "lock" }),
    },
    !p.can_edit &&
      !thread.is_deleted && { key: "report", label: "Báo cáo vi phạm", onClick: handleReport },
  ].filter(Boolean) as { key: string; label: string; danger?: boolean; onClick: () => void }[];

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="middle">
      <Button type="link" icon={<ArrowLeftOutlined />} onClick={onBack} style={{ paddingLeft: 0 }}>
        Về danh sách chủ đề
      </Button>
      <Card>
        <Space style={{ width: "100%", justifyContent: "space-between" }} align="start">
          <Space direction="vertical" size={4}>
            <Space wrap>
              {thread.is_pinned && (
                <Tag icon={<PushpinOutlined />} color="blue">
                  Ghim
                </Tag>
              )}
              {thread.is_locked && <Tag icon={<LockOutlined />}>Đã khoá</Tag>}
              {thread.is_question &&
                (thread.is_answered ? (
                  <Tag color="success" icon={<CheckCircleFilled />}>
                    Đã giải đáp
                  </Tag>
                ) : (
                  <Tag color="orange">Chưa giải đáp</Tag>
                ))}
            </Space>
            <Title level={4} style={{ margin: 0 }}>
              {thread.title}
            </Title>
            <AuthorLine
              name={thread.author.name}
              isStaff={thread.author.is_staff}
              at={thread.created_at}
              edited={thread.edited_at}
            />
          </Space>
          <Space>
            {!thread.is_deleted && (
              <Tooltip
                title={
                  thread.is_subscribed
                    ? "Đang theo dõi — nhận thông báo khi có trả lời"
                    : "Theo dõi để nhận thông báo"
                }
              >
                <Button
                  icon={thread.is_subscribed ? <BellFilled /> : <BellOutlined />}
                  onClick={() =>
                    subscribe.mutate({ id: thread.id, subscribed: !thread.is_subscribed })
                  }
                >
                  {thread.is_subscribed ? "Đang theo dõi" : "Theo dõi"}
                </Button>
              </Tooltip>
            )}
            {moreItems.length > 0 && (
              <Dropdown menu={{ items: moreItems }} trigger={["click"]}>
                <Button icon={<MoreOutlined />} aria-label="Thao tác chủ đề" />
              </Dropdown>
            )}
          </Space>
        </Space>
        <Divider style={{ margin: "12px 0" }} />
        {thread.is_deleted ? (
          <DeletedBody
            label={thread.deleted_label}
            removedBody={thread.removed_body}
            reason={thread.deleted_reason}
          />
        ) : editing ? (
          <Editor
            initial={thread.body}
            saving={updateThread.isPending}
            onCancel={() => setEditing(false)}
            onSave={(b) =>
              updateThread.mutate(
                { id: thread.id, body: b },
                { onSuccess: () => setEditing(false) },
              )
            }
          />
        ) : (
          <MarkdownContent source={thread.body} />
        )}
      </Card>

      <Text strong>{thread.reply_count} trả lời</Text>
      <Space direction="vertical" style={{ width: "100%" }}>
        {topLevel.map((post) => (
          <PostItem
            key={post.id}
            post={post}
            thread={thread}
            replies={repliesOf(post.id)}
            onReply={(target) => {
              setParent(target);
              focusForumEditor("forum-reply-box");
            }}
          />
        ))}
      </Space>

      {p.can_reply ? (
        <Card
          size="small"
          title={parent ? `Trả lời ${parent.author.name}` : "Viết trả lời"}
          extra={
            parent && (
              <Button size="small" type="link" onClick={() => setParent(null)}>
                Trả lời chủ đề
              </Button>
            )
          }
        >
          <div id="forum-reply-box">
            <ForumEditor
              value={body}
              onChange={setBody}
              height={240}
              placeholder="Viết câu trả lời… gõ “/” để chèn bảng, code, công thức"
            />
          </div>
          <Button
            type="primary"
            style={{ marginTop: 8 }}
            disabled={!body.trim()}
            loading={reply.isPending}
            onClick={sendReply}
          >
            Gửi trả lời
          </Button>
        </Card>
      ) : thread.is_deleted ? null : (
        <Alert
          type="info"
          showIcon
          message={
            thread.is_locked
              ? "Chủ đề đã khoá — chỉ giảng viên trả lời được."
              : "Bạn chỉ có quyền xem diễn đàn môn này."
          }
        />
      )}
    </Space>
  );
}
